import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Save, Sun, Moon } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function toISO(d) {
  return d.toISOString().slice(0, 10);
}

/**
 * Vendor availability editor.
 *
 * Data model shown to the vendor:
 *  - Weekly AM/PM toggles (weekday_am, weekday_pm)
 *  - Full-date blackout list (blocked_dates)
 *  - Optional per-date AM or PM block (blocked_date_slots)
 *
 * Backward-compatible with legacy vendors: the GET endpoint folds
 * `unavailable_weekdays` into both weekday_am + weekday_pm automatically.
 */
export default function AvailabilityCalendar({ vendors }) {
  const [vendorId, setVendorId] = useState(vendors?.[0]?.vendor_id || "");
  const [blocked, setBlocked] = useState([]);       // full-day Date[]
  const [initialBlocked, setInitialBlocked] = useState([]);
  const [weekdayAm, setWeekdayAm] = useState([]);   // int[] 0=Sun..6=Sat
  const [weekdayPm, setWeekdayPm] = useState([]);
  const [initialAm, setInitialAm] = useState([]);
  const [initialPm, setInitialPm] = useState([]);
  const [dateSlots, setDateSlots] = useState([]);   // [{date, period}]
  const [initialSlots, setInitialSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Modal shown after tapping a date on the calendar to pick AM / PM / All-day
  const [slotDialogDate, setSlotDialogDate] = useState(null);
  // Read-only external calendar blocks (Tripleseat etc.) — rendered alongside
  // manual blocks in a separate section so vendors cannot delete them from
  // NContro.
  const [externalEvents, setExternalEvents] = useState([]);

  useEffect(() => {
    if (!vendorId && vendors?.length) setVendorId(vendors[0].vendor_id);
  }, [vendors, vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    setLoading(true);
    api.get(`/vendors/${vendorId}/availability`)
      .then(({ data }) => {
        const iso = data.blocked_dates || [];
        setInitialBlocked(iso);
        setBlocked(iso.map(s => new Date(s + "T00:00:00")));
        setWeekdayAm(data.weekday_am || []);
        setWeekdayPm(data.weekday_pm || []);
        setInitialAm(data.weekday_am || []);
        setInitialPm(data.weekday_pm || []);
        setDateSlots(data.blocked_date_slots || []);
        setInitialSlots(data.blocked_date_slots || []);
      })
      .catch(() => {
        setInitialBlocked([]); setBlocked([]);
        setWeekdayAm([]); setWeekdayPm([]); setInitialAm([]); setInitialPm([]);
        setDateSlots([]); setInitialSlots([]);
      })
      .finally(() => setLoading(false));
    // Load Tripleseat/external events for this vendor. Failures are silent —
    // the vendor may not be eligible or the integration may not be enabled.
    api.get("/integrations/tripleseat/events", { params: { vendor_id: vendorId } })
      .then(({ data }) => setExternalEvents(data || []))
      .catch(() => setExternalEvents([]));
  }, [vendorId]);

  const dirty = useMemo(() => {
    const curr = new Set(blocked.map(toISO));
    if (curr.size !== initialBlocked.length || initialBlocked.some(x => !curr.has(x))) return true;
    const setA = new Set(weekdayAm);
    if (setA.size !== initialAm.length || initialAm.some(w => !setA.has(w))) return true;
    const setP = new Set(weekdayPm);
    if (setP.size !== initialPm.length || initialPm.some(w => !setP.has(w))) return true;
    const key = (s) => `${s.date}:${s.period}`;
    const currSlots = new Set(dateSlots.map(key));
    if (currSlots.size !== initialSlots.length || initialSlots.some(s => !currSlots.has(key(s)))) return true;
    return false;
  }, [blocked, initialBlocked, weekdayAm, initialAm, weekdayPm, initialPm, dateSlots, initialSlots]);

  const save = async () => {
    setSaving(true);
    try {
      const dates = blocked.map(toISO);
      const { data } = await api.put(`/vendors/${vendorId}/availability`, {
        dates,
        weekday_am: weekdayAm,
        weekday_pm: weekdayPm,
        blocked_date_slots: dateSlots,
      });
      setInitialBlocked(data.blocked_dates || []);
      setInitialAm(data.weekday_am || []);
      setInitialPm(data.weekday_pm || []);
      setInitialSlots(data.blocked_date_slots || []);
      toast.success("Availability saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggleAm = (w) => setWeekdayAm(prev => prev.includes(w) ? prev.filter(x => x !== w) : [...prev, w].sort());
  const togglePm = (w) => setWeekdayPm(prev => prev.includes(w) ? prev.filter(x => x !== w) : [...prev, w].sort());

  const isFullDayBlocked = (d) => blocked.some(b => toISO(b) === toISO(d));

  // When user taps a date, ask: AM / PM / All day
  const chooseSlot = (period) => {
    if (!slotDialogDate) return;
    const iso = toISO(slotDialogDate);
    if (period === "all") {
      // Ensure it's in blocked[]; drop any partial slot entries for that date.
      if (!isFullDayBlocked(slotDialogDate)) setBlocked(prev => [...prev, slotDialogDate]);
      setDateSlots(prev => prev.filter(s => s.date !== iso));
    } else {
      // Partial slot — remove any full-day block for the same date, then upsert.
      setBlocked(prev => prev.filter(d => toISO(d) !== iso));
      setDateSlots(prev => {
        const exists = prev.some(s => s.date === iso && s.period === period);
        return exists ? prev : [...prev, { date: iso, period }];
      });
    }
    setSlotDialogDate(null);
  };

  const removeSlot = (iso, period) => setDateSlots(prev => prev.filter(s => !(s.date === iso && s.period === period)));
  const removeFullDay = (iso) => setBlocked(prev => prev.filter(d => toISO(d) !== iso));

  const partialByDate = useMemo(() => {
    const m = {};
    dateSlots.forEach(s => { (m[s.date] = m[s.date] || []).push(s.period); });
    return m;
  }, [dateSlots]);

  // Dates that have any external (Tripleseat) block — used to visually
  // distinguish them and to prevent the vendor from turning them into manual
  // full-day blocks (they'd be dropped on save server-side, but the UX is
  // clearer if we surface why the day is booked).
  const externalByDate = useMemo(() => {
    const m = {};
    externalEvents.forEach(e => {
      if (!e.event_date) return;
      (m[e.event_date] = m[e.event_date] || []).push(...(e.periods || []));
    });
    return m;
  }, [externalEvents]);

  if (!vendors?.length) return <div className="text-[color:var(--text-muted)] text-sm" data-testid="availability-empty">Create a listing first to manage availability.</div>;

  return (
    <div data-testid="availability-panel" className="space-y-8">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={vendorId} onValueChange={setVendorId}>
          <SelectTrigger data-testid="availability-vendor-select" className="bg-white h-11 max-w-xs"><SelectValue placeholder="Choose a listing" /></SelectTrigger>
          <SelectContent>
            {vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="availability-save" onClick={save} disabled={!dirty || saving} className="btn-sage rounded-lg h-11 px-5 ml-auto">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save changes
        </Button>
      </div>

      {/* Weekly AM/PM grid */}
      <div>
        <div className="eyebrow mb-2">Weekly availability</div>
        <p className="text-[color:var(--text-muted)] text-xs mb-4 max-w-lg">
          Toggle the mornings (AM) or afternoons/evenings (PM) you're never available. Applies to every future week.
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-[520px]" data-testid="weekly-am-pm-grid">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#5C5C5C]">
                <th className="py-1 pr-4 text-left">Day</th>
                <th className="py-1 px-3 text-center"><span className="inline-flex items-center gap-1"><Sun className="w-3 h-3" /> AM</span></th>
                <th className="py-1 px-3 text-center"><span className="inline-flex items-center gap-1"><Moon className="w-3 h-3" /> PM</span></th>
              </tr>
            </thead>
            <tbody>
              {DAYS.map((label, w) => {
                const amOff = weekdayAm.includes(w);
                const pmOff = weekdayPm.includes(w);
                const cell = (on) => `h-9 w-24 rounded-full text-[11px] uppercase tracking-widest border transition-colors ${on ? "bg-[color:var(--accent)] text-white border-[color:var(--accent)]" : "bg-white text-[color:var(--text)] border-[#E8E2D9] hover:bg-[#F7F3EC]"}`;
                return (
                  <tr key={w} className="border-t border-[#EFEADF]" data-testid={`weekday-row-${w}`}>
                    <td className="py-2 pr-4 font-serif text-lg">{label}</td>
                    <td className="py-2 px-3 text-center">
                      <button data-testid={`toggle-am-${w}`} aria-pressed={amOff} onClick={() => toggleAm(w)} className={cell(amOff)}>
                        {amOff ? "Off" : "Available"}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button data-testid={`toggle-pm-${w}`} aria-pressed={pmOff} onClick={() => togglePm(w)} className={cell(pmOff)}>
                        {pmOff ? "Off" : "Available"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* One-off dates */}
      <div className="grid lg:grid-cols-[auto,1fr] gap-8">
        <div className="card-elegant p-4">
          {loading ? (
            <div className="p-6 text-sm text-[color:var(--text-muted)]">Loading…</div>
          ) : (
            <Calendar
              mode="single"
              selected={undefined}
              onSelect={(d) => d && setSlotDialogDate(d)}
              disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
              modifiers={{
                fullday: (d) => isFullDayBlocked(d),
                partial: (d) => !!partialByDate[toISO(d)],
                external: (d) => !!externalByDate[toISO(d)],
              }}
              modifiersClassNames={{
                fullday: "bg-[#C05A46]/15 text-[#C05A46] line-through",
                partial: "bg-[#EFEADF] text-[#5C5C5C]",
                external: "ring-2 ring-inset ring-[color:var(--accent)]/40 font-semibold",
              }}
              data-testid="availability-calendar"
            />
          )}
        </div>
        <div className="space-y-5 text-sm">
          <div>
            <div className="eyebrow mb-2">One-off blocks</div>
            <p className="text-[color:var(--text-muted)] text-xs mb-3 max-w-md">
              Tap any future date on the calendar to block only AM, only PM, or the whole day. Full-day blackouts override the weekly rules.
            </p>
            <div className="space-y-2 max-h-64 overflow-auto pr-2" data-testid="blocked-list">
              {blocked.length === 0 && dateSlots.length === 0 && (
                <div className="text-[color:var(--text-muted)] text-xs italic">No one-off blocks yet.</div>
              )}
              {blocked.slice().sort((a,b) => a - b).map((d) => {
                const iso = toISO(d);
                return (
                  <div key={`fd-${iso}`} className="flex items-center justify-between gap-2 bg-white border border-[#E8E2D9] rounded-md px-3 py-2" data-testid={`blocked-row-fullday-${iso}`}>
                    <div>
                      <div className="font-serif text-base">{d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#C05A46]">Full day</div>
                    </div>
                    <button onClick={() => removeFullDay(iso)} className="text-xs text-[#5C5C5C] hover:text-[#C05A46]">Remove</button>
                  </div>
                );
              })}
              {dateSlots.slice().sort((a,b) => a.date.localeCompare(b.date) || a.period.localeCompare(b.period)).map(s => (
                <div key={`s-${s.date}-${s.period}`} className="flex items-center justify-between gap-2 bg-white border border-[#E8E2D9] rounded-md px-3 py-2" data-testid={`blocked-row-${s.period}-${s.date}`}>
                  <div>
                    <div className="font-serif text-base">{new Date(s.date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C]">{s.period === "am" ? "Morning only" : "Afternoon only"}</div>
                  </div>
                  <button onClick={() => removeSlot(s.date, s.period)} className="text-xs text-[#5C5C5C] hover:text-[#C05A46]">Remove</button>
                </div>
              ))}
            </div>
          </div>

          {externalEvents.length > 0 && (
            <div data-testid="external-blocks">
              <div className="eyebrow mb-2">Synced from Tripleseat</div>
              <p className="text-[color:var(--text-muted)] text-xs mb-3 max-w-md">
                These are booked events from your connected calendar. To change one, edit the event in Tripleseat and it will re-sync here.
              </p>
              <div className="space-y-2 max-h-64 overflow-auto pr-2">
                {externalEvents.map(e => (
                  <div key={`ext-${e.external_event_id}`} className="flex items-center justify-between gap-2 rounded-md px-3 py-2 border border-[color:var(--accent)]/25 bg-[color:var(--accent)]/5" data-testid={`external-row-${e.external_event_id}`}>
                    <div>
                      <div className="font-serif text-base">
                        {e.event_date
                          ? new Date(e.event_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                          : "—"}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-[color:var(--accent)]">Booked · Tripleseat · {(e.periods || []).join(" + ").toUpperCase()}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-[#5C5C5C]">Read-only</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Slot picker dialog */}
      <Dialog open={!!slotDialogDate} onOpenChange={(o) => !o && setSlotDialogDate(null)}>
        <DialogContent data-testid="slot-picker-dialog" className="max-w-sm bg-[#FDFBF7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Block {slotDialogDate?.toLocaleDateString("en-US", { month: "long", day: "numeric" })}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <Button variant="outline" className="rounded-full h-14" onClick={() => chooseSlot("am")} data-testid="pick-slot-am">
              <span className="flex flex-col items-center"><Sun className="w-4 h-4 mb-1" /> AM only</span>
            </Button>
            <Button variant="outline" className="rounded-full h-14" onClick={() => chooseSlot("pm")} data-testid="pick-slot-pm">
              <span className="flex flex-col items-center"><Moon className="w-4 h-4 mb-1" /> PM only</span>
            </Button>
            <Button className="btn-sage rounded-full h-14" onClick={() => chooseSlot("all")} data-testid="pick-slot-all">
              All day
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSlotDialogDate(null)} className="rounded-full">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
